import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import { TextHot } from 'components/common/TextHot';
import { colors } from 'common/theme';
import { EligiblesBadge } from '../../new_header/components/hot-yoter-eligibles-popup/components/EligiblesBadge';
import { HotYoterPurchasedBenefitCard } from './HotYoterPurchasedBenefitCard';
import { parseSubtitleWithViewAction } from '../utils/getHotYoterStateText';
import { HOT_YOTER_VIEW_PURCHASED_LABEL } from '../constants';

const STATUS_TITLE_SX = {
    m: 0,
    fontSize: { xs: '20px', sm: '15px', md: '15px', lg: '18px' },
    lineHeight: { xs: 1.3, lg: 1.2 },
    direction: 'rtl',
};

const STATUS_SUBTITLE_SX = {
    m: 0,
    fontSize: { xs: '17px', sm: '13px', md: '12px', lg: '14px' },
    lineHeight: { xs: 1.4, lg: 1.25 },
};

const VIEW_ACTION_SX = {
    m: 0,
    fontSize: { xs: '17px', sm: '13px', md: '12px', lg: '14px' },
    lineHeight: { xs: 1.4, lg: 1.25 },
    color: colors.system.text.interactive,
    textDecoration: 'underline',
    textDecorationColor: colors.system.text.interactive,
    whiteSpace: 'nowrap',
};

const getBadgeCircleSize = (deviceState) => {
    if (deviceState === 'mobile') {
        return 34;
    }

    if (deviceState === 'tablet') {
        return 38;
    }

    return 40;
};

export const HotYoterStateBanner = ({
    pageTitle,
    content = {},
    purchasedBenefits = [],
    testID = 'hot-yoter-state-banner',
}) => {
    const deviceState = useSelector((store) => store.state.deviceState);
    const showPurchasedBenefits = purchasedBenefits.length > 0;
    const hasStateText = Boolean(content?.title || content?.subtitle);
    const showStatus = hasStateText || showPurchasedBenefits;

    const [cardsExpanded, setCardsExpanded] = useState(false);

    const canViewPurchased = showPurchasedBenefits;
    const viewActionLabel = HOT_YOTER_VIEW_PURCHASED_LABEL;
    const subtitleParts = useMemo(
        () => (canViewPurchased
            ? parseSubtitleWithViewAction(content?.subtitle, viewActionLabel)
            : { textBefore: content?.subtitle?.trim() ?? '', showComma: false }),
        [content?.subtitle, viewActionLabel, canViewPurchased],
    );

    if (!pageTitle && !showStatus) {
        return null;
    }

    const showMobileCards = canViewPurchased && cardsExpanded;

    const mobileSubtitleRow = (subtitleParts.textBefore || canViewPurchased) ? (
        <div
            className="hot-yoter-state-banner__subtitle-row"
            testId={`${testID}-subtitle-row`}
        >
            {subtitleParts.textBefore ? (
                <TextHot
                    size="xs"
                    color="secondary"
                    component="span"
                    className="hot-yoter-state-banner__subtitle"
                    sx={STATUS_SUBTITLE_SX}
                    testID={`${testID}-subtitle`}
                >
                    {subtitleParts.textBefore}
                </TextHot>
            ) : null}
            {canViewPurchased ? (
                <button
                    type="button"
                    className="hot-yoter-state-banner__view-action"
                    onClick={() => setCardsExpanded((current) => !current)}
                    testId={`${testID}-view-purchased`}
                >
                    {cardsExpanded ? (
                        <KeyboardArrowDownRoundedIcon
                            className="hot-yoter-state-banner__view-action-icon"
                            sx={{ fontSize: 22, color: colors.system.text.interactive }}
                        />
                    ) : (
                        <ChevronLeftRoundedIcon
                            className="hot-yoter-state-banner__view-action-icon"
                            sx={{ fontSize: 22, color: colors.system.text.interactive }}
                        />
                    )}
                    <TextHot
                        size="xs"
                        component="span"
                        className="hot-yoter-state-banner__view-action-text"
                        sx={VIEW_ACTION_SX}
                        testID={`${testID}-view-purchased-text`}
                    >
                        {viewActionLabel}
                    </TextHot>
                </button>
            ) : null}
        </div>
    ) : null;

    const statusBlock = showStatus ? (
        <div
            className="hot-yoter-state-banner__status"
            testId={`${testID}-status`}
            dir="rtl"
        >
            <EligiblesBadge
                count={content.badgeCount ?? 0}
                circleSize={getBadgeCircleSize(deviceState)}
                testID={`${testID}-badge`}
            />
            <div className="hot-yoter-state-banner__text">
                {content.title ? (
                    <TextHot
                        size="xs"
                        bold="bold"
                        color="primary"
                        component="p"
                        className="hot-yoter-state-banner__title"
                        sx={STATUS_TITLE_SX}
                        testID={`${testID}-title`}
                    >
                        {content.title}
                    </TextHot>
                ) : null}
                {content.subtitle ? (
                    <TextHot
                        size="xs"
                        color="secondary"
                        component="p"
                        className="hot-yoter-state-banner__subtitle"
                        sx={STATUS_SUBTITLE_SX}
                        testID={`${testID}-subtitle`}
                    >
                        {content.subtitle}
                    </TextHot>
                ) : null}
            </div>
        </div>
    ) : null;

    const mobileStatusBlock = showStatus ? (
        <div
            className="hot-yoter-state-banner__status hot-yoter-state-banner__status_mobile"
            testId={`${testID}-status`}
            dir="rtl"
        >
            <EligiblesBadge
                count={content.badgeCount ?? 0}
                circleSize={getBadgeCircleSize(deviceState)}
                testID={`${testID}-badge`}
            />
            <div className="hot-yoter-state-banner__text">
                {content.title ? (
                    <TextHot
                        size="xs"
                        bold="bold"
                        color="primary"
                        component="p"
                        className="hot-yoter-state-banner__title"
                        sx={STATUS_TITLE_SX}
                        testID={`${testID}-title`}
                    >
                        {content.title}
                    </TextHot>
                ) : null}
                {mobileSubtitleRow}
            </div>
        </div>
    ) : null;

    const benefitsCardsBlock = showPurchasedBenefits ? (
        <div
            className={[
                'hot-yoter-state-banner__benefits',
                purchasedBenefits.length === 1 && 'hot-yoter-state-banner__benefits_single',
                purchasedBenefits.length <= 2 && 'hot-yoter-state-banner__benefits_fit',
            ].filter(Boolean).join(' ')}
            testId={`${testID}-benefits`}
        >
            {purchasedBenefits.map((benefit, index) => (
                <HotYoterPurchasedBenefitCard
                    key={benefit.id ?? `${benefit.name}-${index}`}
                    benefit={benefit}
                    showOrderDate
                    inline
                    testID={`${testID}-benefit-${index}`}
                />
            ))}
        </div>
    ) : null;

    return (
        <div
            className={`hot-yoter-state-banner${showPurchasedBenefits ? ' hot-yoter-state-banner_with-benefits' : ''}`}
            testId={testID}
            dir="rtl"
        >
            {showPurchasedBenefits ? (
                <div
                    className="hot-yoter-state-banner__mobile-layout"
                    testId={`${testID}-mobile-layout`}
                >
                    {mobileStatusBlock}
                    {showMobileCards ? benefitsCardsBlock : null}
                </div>
            ) : (
                <div className="hot-yoter-state-banner__top">
                    {statusBlock}
                </div>
            )}
        </div>
    );
};
